import { useColorModeValue } from 'jaen';
import {
  Text,
  Box,
  Flex,
  HStack,
  useDisclosure,
  IconButton,
  Grid,
  Container,
  Icon,
  Spacer,
  Button,
  LinkOverlay,
  LinkBox
} from '@chakra-ui/react';
import { FC, useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useContactModal } from '../../services/contact';
import { useLocalizeHref, usePageLocale } from '../../contexts/locale';
import { stripLocalePrefix } from '../../utils/navigation';
import LanguageSwitcher from '../LanguageSwitcher';
import { FaTwitter } from '@react-icons/all-files/fa/FaTwitter';
import { FaGithub } from '@react-icons/all-files/fa/FaGithub';
import { FaInstagram } from '@react-icons/all-files/fa/FaInstagram';
import { FaFacebook } from '@react-icons/all-files/fa/FaFacebook';
import { FiSearch } from '@react-icons/all-files/fi/FiSearch';
import Netsnek from '../../gatsby-plugin-jaen/components/Netsnek';
import Logo from '../../gatsby-plugin-jaen/components/Logo';
import HamburgerMenuIcon, {
  THamburgerMenuIconStylerProps
} from '../AltHamburgerMenuIcon';
import SearchMenu from '../search-menu';

interface IAltTopNavProps {
  path: string;
  hamburgerIconProps?: THamburgerMenuIconStylerProps;
}

const AltTopNav: FC<IAltTopNavProps> = ({ path, hamburgerIconProps }) => {
  hamburgerIconProps = {
    color: 'red.500'
  };

  const intl = useIntl();
  const localizeHref = useLocalizeHref();

  // Landing page = the locale root ('/', '/en/', ...), never a subpage.
  const { prefix } = usePageLocale();
  const isLandingPage =
    stripLocalePrefix(path ?? '', prefix).replace(/\/+$/, '') === '';

  const contactModal = useContactModal();
  const handleOnContactClick = () => {
    console.log('contactModal', contactModal);
    contactModal.onOpen({
      meta: {}
    });
  };

  const { open, onOpen, onClose } = useDisclosure();
  const [hamburgerClass, setHamburgerClass] = useState('');

  const openDrawer = () => {
    setHamburgerClass('open');
    onOpen();
  };

  const closeDrawer = () => {
    setHamburgerClass('');
    onClose();
  };

  const toggleMobileMenu = () => {
    if (hamburgerClass === 'open') closeDrawer();
    else openDrawer();
  };

  // Handle window resize and close menu if window width is >= 768px (md breakpoint)
  // useEffect(() => {
  //   const handleResize = () => {
  //     if (window.innerWidth >= 768) {
  //       closeDrawer();
  //     }
  //   };

  //   window.addEventListener('resize', handleResize);

  //   return () => {
  //     window.removeEventListener('resize', handleResize);
  //   };
  // }, []);

  return (
    <Box
      // The header fades and slides in on the landing page, where it is part
      // of the first impression. On a subpage the reader arrived to read
      // something, and a header that animates in every time is noise, so
      // there it just renders.
      pos="relative"
      overflow="hidden"
      height={open ? 'calc(100vh + 15px)' : { base: '12vh', md: '15vh' }}
      minH={open ? '600px' : '100px'}
      transition="height 0.2s cubic-bezier(0.68, 0, 0.27, 1), min-height 0.2s cubic-bezier(0.68, 0, 0.27, 1)"
      borderBottomRadius={'2xl'}
    >
      <Box
        pos="relative"
        bg="#0A0A0A"
        color="white"
        transition="height 0.2s cubic-bezier(0.68, 0, 0.27, 1), min-height 0.2s cubic-bezier(0.68, 0, 0.27, 1)"
        height={open ? 'max(600px, calc(100vh + 15px))' : '0'}
        minH={open ? 'fit-content' : '0'}
        width="100%"
        overflow="hidden"
      >
        <Grid
          as={Container}
          maxW="8xl"
          minH="max(600px, calc(100vh + 15px))"
          templateRows={{
            base: 'auto repeat(7, 1fr)',
            md: 'auto repeat(3, 1fr)'
          }} // Set the first row height to 15vh
          templateColumns={{ base: '1fr', md: '1fr 1fr' }}
          templateAreas={{
            base: `"empty" "services" "team" "portfolio" "blog" "offices" "social"`,
            md: `"empty empty"
                         "services team"
                         "portfolio blog"
                         "offices social"`
          }}
          //gap={4}
          fontSize={{ base: 'xx-large', md: 'xxx-large' }}
          h="full"
          w="full"
        >
          <Box
            gridArea="empty"
            h={{ base: '12vh', md: '15vh' }}
            minH={{ base: '100px', md: '100px' }}
          />
          <LinkBox
            gridArea="services"
            display="flex"
            pl={{ base: '8', md: '16' }}
            alignItems="center"
            borderWidth="1px"
            borderStyle="solid"
            borderRight="1px"
            borderLeft="0"
            borderBottom="0"
            borderColor={useColorModeValue('gray.900', 'gray.600')}
            transition="color 0.2s"
            _hover={{
              color: 'brand.500'
            }}
          >
            <LinkOverlay href={localizeHref('/docs')}>
              {intl.formatMessage({
                id: 'AltNavServices',
                defaultMessage: 'Unsere Services'
              })}
            </LinkOverlay>
          </LinkBox>
          <LinkBox
            gridArea="team"
            display="flex"
            pl={{ base: '8', md: '16' }}
            alignItems="center"
            borderWidth="1px"
            borderStyle="solid"
            borderBottom="0"
            borderRight="0"
            borderLeft={{ base: '1px', md: '0px' }}
            borderColor={useColorModeValue('gray.900', 'gray.600')}
            transition="color 0.2s"
            _hover={{
              color: 'brand.500'
            }}
          >
            <LinkOverlay href={localizeHref('/docs')}>
              {intl.formatMessage({
                id: 'AltNavDocs',
                defaultMessage: 'Dokumentation'
              })}
            </LinkOverlay>
          </LinkBox>
          <LinkBox
            gridArea="portfolio"
            display="flex"
            pl={{ base: '8', md: '16' }}
            alignItems="center"
            borderWidth="1px"
            borderStyle="solid"
            borderRight="1px"
            borderLeft="0"
            borderBottom="0"
            borderColor={useColorModeValue('gray.900', 'gray.600')}
            transition="color 0.2s"
            _hover={{
              color: 'brand.500'
            }}
          >
            <LinkOverlay href={localizeHref('/docs')}>
              {intl.formatMessage({
                id: 'AltNavPortfolio',
                defaultMessage: 'Unser Portfolio'
              })}
            </LinkOverlay>
          </LinkBox>
          <LinkBox
            gridArea="blog"
            display="flex"
            pl={{ base: '8', md: '16' }}
            alignItems="center"
            borderWidth="1px"
            borderStyle="solid"
            borderBottom="1px"
            borderRight="0"
            borderLeft={{ base: '1px', md: '0px' }}
            borderColor={useColorModeValue('gray.900', 'gray.600')}
            transition="color 0.2s"
            _hover={{
              color: 'brand.500'
            }}
          >
            <LinkOverlay href={localizeHref('/docs')}>
              {intl.formatMessage({
                id: 'AltNavBlog',
                defaultMessage: 'Blog'
              })}
            </LinkOverlay>
          </LinkBox>
          <Box
            gridArea="offices"
            pt={{ base: '8' }}
            pl={{ base: '8', md: '16' }}
            borderWidth="1px"
            borderTop="1px"
            borderBottom="0"
            borderRight={{ base: '1px', md: '0px' }}
            borderLeft="0"
            borderStyle="solid"
            borderColor={{
              base: 'transparent',
              md: useColorModeValue('gray.900', 'gray.600')
            }}
          >
            {/* Office information here */}
            <Text color="white" fontWeight="bold" fontSize="lg" pb="4">
              {intl.formatMessage({
                id: 'AltNavOfficeHeading',
                defaultMessage: 'Im Herzen von Wien'
              })}
            </Text>
            <Text color="white" fontSize="md">
              {intl.formatMessage({
                id: 'AltNavOfficeHeadquarters',
                defaultMessage: 'Hauptquartier'
              })}
            </Text>
            {/* The street line is a postal address and stays verbatim in
                every locale; only the city and country line is translated. */}
            <Text color="gray.400" fontSize="md">
              Löwengasse 14 / Lokal 2
            </Text>
            <Text color="gray.400" fontSize="md">
              {intl.formatMessage({
                id: 'AltNavOfficeCityCountry',
                defaultMessage: '1030 Wien, Österreich'
              })}
            </Text>
          </Box>
          <Box
            gridArea="social"
            pt={{ base: '8' }}
            pl={{ base: '8', md: '16' }}
            borderWidth="1px"
            borderTop="0"
            borderBottom="0"
            borderRight="0"
            borderLeft="0"
            borderStyle="solid"
            borderColor={useColorModeValue('gray.900', 'gray.600')}
          >
            {/* Social media links here */}
            <Text color="white" fontWeight="bold" fontSize="lg">
              {intl.formatMessage({
                id: 'AltNavFollowUs',
                defaultMessage: 'Folge uns auf'
              })}
            </Text>
            <HStack gap="8">
              <LinkBox
                mr="4"
                display="flex"
                transition="color 0.2s"
                _hover={{
                  color: 'brand.500'
                }}
              >
                <LinkOverlay
                  href="https://facebook.com/netsnek"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Netsnek auf Facebook"
                >
                  <Icon as={FaFacebook} boxSize="6" />
                </LinkOverlay>
              </LinkBox>
              <LinkBox
                mr="4"
                display="flex"
                transition="color 0.2s"
                _hover={{
                  color: 'brand.500'
                }}
              >
                <LinkOverlay
                  href="https://instagram.com/netsnek"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Netsnek auf Instagram"
                >
                  <Icon as={FaInstagram} boxSize="6" />
                </LinkOverlay>
              </LinkBox>
              <LinkBox
                mr="4"
                display="flex"
                transition="color 0.2s"
                _hover={{
                  color: 'brand.500'
                }}
              >
                <LinkOverlay
                  href="https://twitter.com/netsnek_com"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Netsnek auf X"
                >
                  <Icon as={FaTwitter} boxSize="6" />
                </LinkOverlay>
              </LinkBox>
              <LinkBox
                mr="4"
                display="flex"
                transition="color 0.2s"
                _hover={{
                  color: 'brand.500'
                }}
              >
                <LinkOverlay
                  href="https://github.com/netsnek"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Netsnek auf GitHub"
                >
                  <Icon as={FaGithub} boxSize="6" />
                </LinkOverlay>
              </LinkBox>
            </HStack>
          </Box>
        </Grid>
      </Box>
      {/* The rest of your AltTopNav and navigation components */}
      <Box pos="absolute" top="0" left="0" w="100%">
        <Container maxW="8xl" pointerEvents="auto">
          <HStack
            h={{ base: '12vh', md: '15vh' }}
            minH="100px"
            alignItems={'center'}
            px={{ base: '4', md: '8' }}
            py={{ base: '2', md: '4' }}
            justifyContent="space-between"
          >
            <LinkBox height="100%" flex="2" mr="4" display="flex">
              <LinkOverlay
                href={localizeHref('/')}
                color="white"
                // Der Anchor enthaelt nur das Logo-SVG und hatte damit keinen
                // zugaenglichen Namen.
                aria-label={intl.formatMessage({
                  id: 'AltNavHomeLink',
                  defaultMessage: 'Netsnek, zur Startseite'
                })}>
                <Logo height="100%" color="black" />
              </LinkOverlay>
            </LinkBox>
            <Flex alignItems="center" flex="1" justifyContent="flex-end">
              <SearchMenu
                boxSizing={'border-box'}
                borderWidth={{ base: 0, lg: 2 }}
                color={{ base: 'white', lg: 'gray.400' }}
                _hover={{
                  borderColor: 'brand.400',
                  bg: { base: 'brand.400', lg: 'transparent' }
                }}
              />
              <Button
                ml={4}
                filter="drop-shadow(1px 2px 2px rgb(0 0 0 / 0.1))"
                borderRadius="xl"
                onClick={handleOnContactClick}
                fontSize="sm"
                fontWeight="semibold"
                color="white"
                display={{ base: 'none', md: 'block' }}
              >
                {intl.formatMessage({
                  id: 'AltNavRequestNow',
                  defaultMessage: 'Jetzt anfragen'
                })}
              </Button>
              {/* <Button
                  ml={4}
                  filter="drop-shadow(1px 2px 2px rgb(0 0 0 / 0.1))"
                  borderRadius="xl"
                  onClick={handleOnContactClick}
                  fontSize="sm"
                  fontWeight="semibold"
                  color="white"
                  display={{base: 'none', md: 'block'}}>
                  Login
                </Button> */}
              <LanguageSwitcher ml={4} />
              <IconButton
                ml={4}
                filter="drop-shadow(1px 2px 2px rgb(0 0 0 / 0.1))"
                borderRadius="xl"
                //variant="ghost"
                fontWeight={'bold'}
                aria-label={open ? 'Close menu' : 'Open menu'}
                onClick={toggleMobileMenu}
              >
                <HamburgerMenuIcon
                  handleClick={toggleMobileMenu}
                  wrapperProps={{ className: hamburgerClass }}
                  iconProps={{
                    // ...hamburgerIconProps,
                    //boxSize: '6',
                    //boxSize: '100%',
                    backgroundColor: 'white'
                  }}
                />
              </IconButton>
            </Flex>
          </HStack>
        </Container>
      </Box>
      <Box
        pointerEvents="none"
        pos="absolute"
        top="0"
        left="0"
        transition="height 0.2s cubic-bezier(0.68, 0, 0.27, 1)"
        h={open ? 'max(600px, 100vh)' : '0'} // Adjust according to your AltTopNav's initial height
        w="100%"
        overflow="hidden"
      >
        <Container maxW="8xl" pointerEvents="auto">
          <HStack
            h={{ base: '12vh', md: '15vh' }}
            minH="100px"
            alignItems={'center'}
            px={{ base: '4', md: '8' }}
            py={{ base: '2', md: '4' }}
            justifyContent="space-between"
          >
            <LinkBox height="100%" flex="2" mr="4" display="flex">
              <LinkOverlay href={localizeHref('/')} color="white">
                <Logo height="100%" color="white" />
              </LinkOverlay>
            </LinkBox>
            <Flex alignItems="center" flex="1" justifyContent="flex-end">
              {/* Solid white with black content, like every other control in
                  this bar. It used to stay transparent with a white outline
                  from lg upwards, which is what made its keycap unreadable:
                  the cap is styled for a light surface and was sitting on the
                  black panel. On white it needs nothing said about it. */}
              <SearchMenu
                boxSizing={'border-box'}
                borderWidth={{ base: 0, lg: 2 }}
                borderColor="white"
                bgColor="white"
                color="black"
                _hover={{
                  bgColor: 'brand.400',
                  color: 'white',
                  borderColor: 'brand.400'
                }}
                _active={{
                  bgColor: 'brand.300',
                  color: 'white',
                  borderColor: 'brand.300'
                }}
              />
              <Button
                ml={4}
                _hover={{ bg: 'brand.400', color: 'white' }}
                _active={{ bg: 'brand.300', color: 'white' }}
                borderRadius="xl"
                filter="drop-shadow(1px 2px 2px rgb(0 0 0 / 0.1))"
                //variant="ghost"
                onClick={handleOnContactClick}
                fontSize="sm"
                fontWeight="semibold"
                bg="white"
                color="black"
                display={{ base: 'none', md: 'block' }}
              >
                {intl.formatMessage({
                  id: 'AltNavRequestNow',
                  defaultMessage: 'Jetzt anfragen'
                })}
              </Button>
              {/* <Button
                  ml={4}
                  _hover={{
                    bg: 'brand.500'
                  }}
                  borderRadius="xl"
                  filter="drop-shadow(1px 2px 2px rgb(0 0 0 / 0.1))"
                  //variant="ghost"
                  onClick={handleOnContactClick}
                  fontSize="sm"
                  fontWeight="semibold"
                  bg="white"
                  color="black"
                  display={{base: 'none', md: 'block'}}>
                  Login
                </Button> */}
              {/* Im geoeffneten Menue schiebt sich wie bei den Nachbarn eine weisse
                  Fassung darueber, sonst bliebe der Sprachwaehler als einziges
                  oranges Element auf der dunklen Flaeche stehen. */}
              <LanguageSwitcher
                ml={4}
                bg="white"
                color="black"
                _hover={{ bg: 'brand.400', color: 'white' }}
                _active={{ bg: 'brand.300', color: 'white' }}
              />
              <IconButton
                ml={4}
                _hover={{ bg: 'brand.400' }}
                _active={{ bg: 'brand.300' }}
                filter="drop-shadow(1px 2px 2px rgb(0 0 0 / 0.1))"
                borderRadius="xl"
                //variant="ghost"
                fontWeight={'bold'}
                aria-label={open ? 'Close menu' : 'Open menu'}
                onClick={toggleMobileMenu}
                bg={'white'}
                color={'black'}
              >
                <HamburgerMenuIcon
                  // handleClick={toggleMobileMenu}
                  wrapperProps={{ className: hamburgerClass }}
                  iconProps={{
                    // ...hamburgerIconProps,
                    //boxSize: '6',
                    //boxSize: '100%',
                    backgroundColor: 'black'
                  }}
                />
              </IconButton>
            </Flex>
          </HStack>
        </Container>
      </Box>
    </Box>
  );
};

export default AltTopNav;
